import { useState } from 'react';

import { CheckmarkSquare02Icon, Download01Icon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import cn from 'classnames';
import { downloadFile, getConsentDisplayName } from 'helpers/candidate-portal-helpers';
import { useTranslation } from 'react-i18next';

import { IconComponent } from 'components/Icon/IconComponent';
import SliderMotion from 'components/Motion/SliderMotion';

export const CandidateConsentList = ({
  candidateId,
  consentFiles,
  brand = null,
  isLoading = false,
  error = null,
}) => {
  const { t } = useTranslation();
  const [downloadingId, setDownloadingId] = useState(null);

  /**
   * Handles file download with error handling and loading state
   * @param {string} url - File URL to download
   * @param {string} fileName - Name for the downloaded file
   * @param {string|number} fileId - File ID for tracking download state
   */
  const handleDownload = async (url, fileName, fileId) => {
    if (!url) {
      console.error('File URL is required');
      return;
    }

    if (!fileName) {
      console.warn('File name not provided, using default');
    }

    const defaultFileName = fileName || 'consent.pdf';
    setDownloadingId(fileId);

    try {
      await downloadFile(url, defaultFileName);
    } catch (error) {
      console.error('Download failed:', error);
      // TODO: Show user-friendly error message (e.g., toast notification)
    } finally {
      setDownloadingId(null);
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <SliderMotion key={`footer-${candidateId || 'default'}`} duration={0.2}>
        <section
          className="flex flex-col bg-surface-primary rounded-3xl shadow-sm border border-border-primary shadow-soft"
          aria-labelledby="consents-heading">
          <div className="flex flex-col gap-2 p-8">
            <h2 id="consents-heading" className="text-2xl font-semibold text-text-primary m-0">
              {t('candidatePortal.consents')}
            </h2>
          </div>
          <hr className="border-border-primary" aria-hidden="true" />
          <div className="flex p-8" role="status" aria-live="polite">
            <p className="text-xl text-text-secondary font-regular m-0">
              {t('candidatePortal.loading') || 'Loading...'}
            </p>
          </div>
        </section>
      </SliderMotion>
    );
  }

  // Error state
  if (error) {
    return (
      <SliderMotion key={`footer-${candidateId || 'default'}`} duration={0.2}>
        <section
          className="flex flex-col bg-surface-primary rounded-3xl shadow-sm border border-border-primary shadow-soft"
          aria-labelledby="consents-heading">
          <div className="flex flex-col gap-2 p-8">
            <h2 id="consents-heading" className="text-2xl font-semibold text-text-primary m-0">
              {t('candidatePortal.consents')}
            </h2>
          </div>
          <hr className="border-border-primary" aria-hidden="true" />
          <div className="flex p-8" role="alert">
            <p className="text-xl text-text-secondary font-regular m-0">
              {error?.message ||
                t('candidatePortal.errorLoadingConsents') ||
                'Error loading consents'}
            </p>
          </div>
        </section>
      </SliderMotion>
    );
  }

  // Empty state or list
  const hasConsentFiles = consentFiles && Array.isArray(consentFiles) && consentFiles.length > 0;

  return (
    <SliderMotion key={`footer-${candidateId || 'default'}`} duration={0.2}>
      <section
        className="flex flex-col bg-surface-primary rounded-3xl shadow-sm border border-border-primary shadow-soft"
        aria-labelledby="consents-heading">
        <div className="flex flex-col gap-2 p-8">
          <h2 id="consents-heading" className="text-2xl font-semibold text-text-primary m-0">
            {t('candidatePortal.consents')}
          </h2>
        </div>
        <hr className="border-border-primary" aria-hidden="true" />
        {hasConsentFiles ? (
          <ul
            className="flex flex-col gap-4 mt-4 list-none p-0 m-0"
            role="list"
            aria-label={t('candidatePortal.consentFilesList')}>
            {consentFiles.map(consentFile => {
              if (!consentFile?.id) {
                return null;
              }

              const displayName = getConsentDisplayName(consentFile.file_name);
              const isDownloading = downloadingId === consentFile.id;
              const fileName = consentFile.file_name || 'consent.pdf';

              return (
                <li
                  key={consentFile.id}
                  className="flex items-center justify-between gap-4 pb-4 border-b border-border-primary last:border-b-0 px-8"
                  role="listitem">
                  <div className="flex items-center gap-6 w-full flex-1">
                    <IconComponent
                      icon={CheckmarkSquare02Icon}
                      className={
                        brand?.color_button
                          ? `!bg-[var(--brand-badge-bg)] !text-[var(--brand-text)] !border-0`
                          : ''
                      }
                      type={brand?.color_button ? 'secondary' : ''}
                      aria-hidden="true"
                    />
                    <div className="flex flex-col gap-2 w-full">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="text-2xl font-regular text-text-primary m-0">
                          {displayName}
                        </h3>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    {consentFile.url && (
                      <button
                        type="button"
                        onClick={() => handleDownload(consentFile.url, fileName, consentFile.id)}
                        disabled={isDownloading}
                        aria-label={
                          isDownloading
                            ? t('candidatePortal.downloadingFile', { file: displayName })
                            : t('candidatePortal.downloadFile', { file: displayName })
                        }
                        aria-busy={isDownloading}
                        className={cn(
                          'w-full sm:w-max justify-center text-xl text-text-primary cursor-pointer font-regular bg-surface-secondary border border-border-primary rounded-full px-8 py-4 flex items-center gap-2 transition-colors duration-200 no-underline hover:no-underline focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-text-primary',
                          brand?.color_button
                            ? 'hover:bg-[var(--brand-button-bg)] hover:text-[var(--brand-button-text)] hover:border-[var(--brand-button-bg)]'
                            : 'hover:bg-primary-4',
                          isDownloading && 'opacity-50 cursor-not-allowed',
                        )}>
                        <HugeiconsIcon
                          icon={Download01Icon}
                          size="16"
                          strokeWidth={1.5}
                          aria-hidden="true"
                        />
                        {isDownloading
                          ? t('candidatePortal.downloading') || 'Downloading...'
                          : t('candidatePortal.download')}
                      </button>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        ) : (
          <div className="flex flex-col gap-4 p-8" role="status">
            <p className="text-xl text-text-secondary font-regular m-0">
              {t('candidatePortal.noConsentsFound')}
            </p>
          </div>
        )}
      </section>
    </SliderMotion>
  );
};
